import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdBanner from '../AdBanner/AdBanner';
import MarketplaceCategorySidebar from './MarketplaceCategorySidebar';
import MarketplaceHeroBento from './MarketplaceHeroBento';
import MarketplaceHomePromoPanel from './MarketplaceHomePromoPanel';
import MarketplaceHeroSearch from './MarketplaceHeroSearch';
import MarketplaceProductCard from './MarketplaceProductCard';
import MarketplaceSellerCard from './MarketplaceSellerCard';
import MarketplaceFloatingDock from './MarketplaceFloatingDock';
import MarketplaceReveal from './MarketplaceReveal';
import { useCurrency } from '../../context/CurrencyContext';
import { SOCKET_URL as API_BASE } from '../../config/api';
import './marketplace-layout.css';
import './marketplace-home-animations.css';

function buildStripProducts(products, dup) {
  if (!dup || !products.length) return products;
  const copies = products.length <= 5 ? 4 : products.length >= 18 ? 2 : 3;
  const out = [];
  for (let i = 0; i < copies; i++) out.push(...products);
  return out;
}

/* ── Horizontal product strip ── */
function ProductStrip({ title, subtitle, viewHref, products, loading, addedProducts, isInInquiry, onInquire, variant }) {
  const strip = !loading && products.length > 0 ? buildStripProducts(products, true) : products;
  return (
    <MarketplaceReveal>
      <section className="mh-content-max mh-strip-section">
        <div className="mh-section-card">
          <div className="mh-section-head">
            <div>
              <h2 className="mh-section-title">{title}</h2>
              {subtitle && <p className="mh-section-sub">{subtitle}</p>}
            </div>
            <Link to={viewHref} className="mh-view-more-link">View more <span>›</span></Link>
          </div>
          <div className="mh-strip-scroll mh-product-strip-scroll">
            {loading ? (
              <div className="mh-strip-loader">{[1,2,3,4,5].map(i => <div key={i} className="mh-skeleton-card" />)}</div>
            ) : products.length === 0 ? (
              <p className="mh-empty-msg">No listings yet</p>
            ) : (
              strip.map((p, i) => (
                <MarketplaceProductCard
                  key={`${title}-${p.id}-${i}`}
                  product={p}
                  addedProducts={addedProducts}
                  isInInquiry={isInInquiry}
                  onInquire={onInquire}
                  variant={variant}
                />
              ))
            )}
          </div>
        </div>
      </section>
    </MarketplaceReveal>
  );
}

/* ── Top Deals compact card strip ── */
function TopDealsSection({ products, loading, addedProducts, isInInquiry, onInquire }) {
  const { formatPriceRange } = useCurrency();
  const navigate = useNavigate();
  const items = !loading && products.length > 0 ? buildStripProducts(products, true) : products;

  return (
    <MarketplaceReveal>
      <section className="mh-content-max mh-strip-section">
        <div className="mh-section-card mh-top-deals-card">
          <div className="mh-section-head">
            <div>
              <h2 className="mh-section-title">Top Deals</h2>
              <p className="mh-section-sub">Score the lowest prices on the marketplace</p>
            </div>
            <Link to="/featured-products" className="mh-view-more-link">View more <span>›</span></Link>
          </div>
          <div className="mh-strip-scroll mh-product-strip-scroll mh-deals-scroll">
            {loading ? (
              <div className="mh-strip-loader">{[1,2,3,4,5,6].map(i => <div key={i} className="mh-skeleton-deal" />)}</div>
            ) : products.length === 0 ? (
              <p className="mh-empty-msg">No deals available</p>
            ) : (
              items.map((p, i) => {
                const rawMin = parseFloat(p.priceMin) || 0;
                const rawMax = parseFloat(p.priceMax) || rawMin;
                const oldMin = (rawMin * 1.09).toFixed(2);
                const oldMax = (rawMax * 1.13).toFixed(2);
                return (
                  <div
                    key={`deal-${p.id}-${i}`}
                    className="mh-deal-card"
                    onClick={() => navigate(`/product/${p.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(`/product/${p.id}`)}
                  >
                    <div className="mh-deal-img-wrap">
                      <img src={p.image || 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400'} alt={p.name} className="mh-deal-img" />
                    </div>
                    <div className="mh-deal-body">
                      <div className="mh-deal-price-row">
                        <svg className="mh-deal-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path d="M12 5v14M5 12l7 7 7-7" stroke="#d9261c" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span className="mh-deal-price">{formatPriceRange(p.priceMin, p.priceMax)}</span>
                      </div>
                      <p className="mh-deal-old">{formatPriceRange(oldMin, oldMax)}</p>
                      <p className="mh-deal-moq">MOQ: {p.moq || '1'}</p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </section>
    </MarketplaceReveal>
  );
}

/* ════════════════════════════════════════════════
   MAIN DESKTOP LAYOUT
════════════════════════════════════════════════ */
export default function MarketplaceHomeDesktop({
  featuredProducts, newProducts, popularProducts,
  topSellers, brands, loading,
  addedProducts, isInInquiry, onInquire,
  favoriteIds, onToggleFavorite, isAuthenticated,
  onSendRequirement, onBecomeSupplier, onFindBuyers,
}) {
  const navigate = useNavigate();

  return (
    <div className="marketplace-home-root">
      <MarketplaceFloatingDock />

      {/* ════ HERO SEARCH SECTION (peach bg, big tabs + search bar) ════ */}
      <MarketplaceHeroSearch />

      {/* ════ WELCOME ROW ════ */}
      <div className="mhd-welcome-row">
        <div className="mhd-welcome-inner">
          <h2 className="mhd-welcome-title">Welcome to Conflux</h2>
          <nav className="mhd-welcome-shortcuts">
            <Link to="/submit-rfq" className="mhd-shortcut">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
              </svg>
              Request for Quotation
            </Link>
            <span className="mhd-shortcut-sep">|</span>
            <Link to="/featured-products" className="mhd-shortcut">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
              </svg>
              Top Ranking
            </Link>
            <span className="mhd-shortcut-sep">|</span>
            <Link to="/products" className="mhd-shortcut">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/>
              </svg>
              Fast customization
            </Link>
          </nav>
        </div>
      </div>

      {/* ════ PAGE GUTTER CONTENT ════ */}
      <div className="mh-page-gutter">

        {/* Hero bento grid: sidebar + freq cards + promo */}
        <MarketplaceReveal>
          <div className="mh-bento-grid">
            <MarketplaceCategorySidebar />
            <MarketplaceHeroBento featuredProducts={featuredProducts} />
            <MarketplaceHomePromoPanel
              onSendRequirement={onSendRequirement}
              onBecomeSupplier={onBecomeSupplier}
              onFindBuyers={onFindBuyers}
            />
          </div>
        </MarketplaceReveal>

        {/* Ad banner */}
        <MarketplaceReveal>
          <div className="mh-content-max mh-ad-row">
            <AdBanner placementSlug="category-log-top" className="mh-ad-banner-inner" />
          </div>
        </MarketplaceReveal>

        {/* Recommended separator */}
        <div className="mh-rec-separator">
          <span>Recommended for your business</span>
        </div>

        {/* Top Deals */}
        <TopDealsSection
          products={featuredProducts}
          loading={loading}
          addedProducts={addedProducts}
          isInInquiry={isInInquiry}
          onInquire={onInquire}
        />

        {/* Promo band 1 */}
        <MarketplaceReveal>
          <div className="mh-content-max mh-promo-band">
            <img src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=2400&q=80" alt="" className="mh-promo-img" />
          </div>
        </MarketplaceReveal>

        {/* Trending products */}
        <ProductStrip
          title="Trending products" subtitle="High inquiry volume this week"
          viewHref="/products" products={popularProducts} loading={loading}
          addedProducts={addedProducts} isInInquiry={isInInquiry} onInquire={onInquire}
        />

        {/* Frequently searched */}
        <ProductStrip
          title="Frequently searched" subtitle="Popular listings buyers view now"
          viewHref="/products" products={newProducts} loading={loading}
          addedProducts={addedProducts} isInInquiry={isInInquiry} onInquire={onInquire}
        />

        {/* Top manufacturers */}
        <MarketplaceReveal>
          <section className="mh-content-max mh-strip-section">
            <div className="mh-section-card">
              <div className="mh-section-head">
                <div>
                  <h2 className="mh-section-title">Top manufacturers</h2>
                  <p className="mh-section-sub">Verified profiles with active catalogs</p>
                </div>
                <Link to="/top-seller" className="mh-view-more-link">View more <span>›</span></Link>
              </div>
              <div className="mh-scroll-h mh-strip-scroll mh-product-strip-scroll">
                {loading ? <p className="mh-empty-msg">Loading…</p> : topSellers.length === 0 ? (
                  <p className="mh-empty-msg">No suppliers yet</p>
                ) : buildStripProducts(topSellers, true).map((s, i) => (
                  <MarketplaceSellerCard key={`${s.id}-${i}`} seller={s} favoriteIds={favoriteIds} onToggleFavorite={onToggleFavorite} isAuthenticated={isAuthenticated} />
                ))}
              </div>
            </div>
          </section>
        </MarketplaceReveal>

        {/* Spotlight brands */}
        <MarketplaceReveal>
          <section className="mh-content-max mh-strip-section">
            <div className="mh-section-card">
              <div className="mh-section-head">
                <div><h2 className="mh-section-title">Spotlight brands</h2></div>
                <Link to="/all-brands" className="mh-view-more-link">View all <span>›</span></Link>
              </div>
              <div className="mh-brands-row">
                {loading ? <span className="mh-empty-msg">Loading…</span> : brands.length === 0 ? (
                  <span className="mh-empty-msg">No brands available</span>
                ) : brands.map((brand) => {
                  const initials = brand.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
                  const bgColor = ['#00BCD4','#4CAF50','#FF9800','#E91E63','#9C27B0','#3F51B5'][brand.id % 6];
                  const isValidLogo = brand.logo && (brand.logo.startsWith('/uploads/') || brand.logo.startsWith('http://localhost'));
                  const logoUrl = isValidLogo ? (brand.logo.startsWith('http') ? brand.logo : `${API_BASE}${brand.logo}`) : null;
                  const handleBrandClick = () => brand.type === 'seller' ? navigate(`/company-profile/${brand.id}`) : navigate(`/products?brand=${brand.id}`);
                  return (
                    <button key={brand.id} type="button" onClick={handleBrandClick} title={brand.name} className="mh-brand-btn">
                      {logoUrl ? <img src={logoUrl} alt={brand.name} className="mh-brand-img" /> : (
                        <span className="mh-brand-initials" style={{ background: bgColor }}>{initials}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>
        </MarketplaceReveal>

        {/* Promo band 2 */}
        <MarketplaceReveal>
          <div className="mh-content-max mh-promo-band">
            <img src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=2400&q=80" alt="" className="mh-promo-img" />
          </div>
        </MarketplaceReveal>

        {/* New arrivals */}
        <ProductStrip
          title="New arrivals" subtitle="Fresh marketplace listings"
          viewHref="/products" products={newProducts} loading={loading}
          addedProducts={addedProducts} isInInquiry={isInInquiry} onInquire={onInquire}
        />

      </div>
    </div>
  );
}
